import { useState, useEffect, useCallback } from 'react';
import { fetchData, updateData } from '../api';

export interface ControllerOptions {
  exportPowerHardLimit: number;
  exportPowerSoftLimit: number;
  exportCurrentHardLimit: number;
  exportCurrentSoftLimit: number;
  voltageHardLimit: number;
  voltageSoftLimit: number;
  perPhaseLimit: boolean;
  currentControl: boolean;
  exportLimitEnabled: boolean;
  deratingKp: number;
  deratingKi: number;
  deratingLimit: number;
  increasingLimit: number;
  meterTarget: number;
  controlCommandInterval: number;  // Minimum delay between control commands in ms
  postControlDelay: number;  // Delay after control before resuming data collection (ms)
  // Periodic heartbeat control - send commands even when limit unchanged
  periodicControlEnabled: boolean;
  periodicControlInterval: number;  // How often to send heartbeat commands (ms)
}

export function useControllerOptions() {
  const [options, setOptions] = useState<ControllerOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOptions = useCallback(async () => {
    try {
      const result = await fetchData('/controllerOptions');
      setOptions(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveOptions = useCallback(async (newOptions: ControllerOptions) => {
    setSaving(true);
    try {
      await updateData('/controllerOptions', newOptions);
      setOptions(newOptions);
      setError(null);
      return true;
    } catch (err) {
      setError(err as Error);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return { options, loading, saving, error, saveOptions, refetch: fetchOptions };
}
